import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  ThemeSwitcher,
  Header as UIHeader,
} from '@quadralize/ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useMatches } from 'react-router-dom'
import { useAppStore } from '@/stores/use-app-store'
import { HeaderUserMenu } from './header-user-menu'

interface HeaderProps {
  className?: string
}

interface BreadcrumbTrailItem {
  label: string | (() => string)
  path?: string
}

interface BreadcrumbHandle {
  breadcrumb?: string | (() => string)
  breadcrumbs?: BreadcrumbTrailItem[]
}

export function Header({ className }: HeaderProps) {
  const { t } = useTranslation('layout')
  const { theme, toggleTheme, toggleSidebar } = useAppStore()
  const matches = useMatches()

  function resolveLabel(label: string | (() => string)): string {
    return typeof label === 'function' ? label() : label
  }

  const explicitTrail = [...matches]
    .reverse()
    .map((match) => (match.handle as BreadcrumbHandle | undefined)?.breadcrumbs)
    .find(
      (breadcrumbs): breadcrumbs is BreadcrumbTrailItem[] =>
        Array.isArray(breadcrumbs) && breadcrumbs.length > 0,
    )

  const breadcrumbItems = explicitTrail
    ? explicitTrail.map((item) => ({
        ...item,
        label: resolveLabel(item.label),
      }))
    : matches
        .map((match) => {
          const handle = match.handle as BreadcrumbHandle | undefined
          if (!handle?.breadcrumb) {
            return null
          }

          return {
            label: resolveLabel(handle.breadcrumb),
            path: match.pathname,
          }
        })
        .filter((item): item is { label: string; path: string } => item !== null)

  const breadcrumbs =
    breadcrumbItems.length >= 2 ? (
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1

            return (
              <React.Fragment key={`${item.path}-${item.label}`}>
                <BreadcrumbItem>
                  {isLast || !item.path ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={item.path}>{item.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            )
          })}
        </BreadcrumbList>
      </Breadcrumb>
    ) : null

  return (
    <UIHeader
      className={className}
      breadcrumbs={breadcrumbs}
      theme={theme}
      onToggleTheme={toggleTheme}
      onToggleSidebarMobile={toggleSidebar}
      userAction={<HeaderUserMenu />}
      logo={{
        text: t('shell.title'),
        imageSrc: '/logo.svg',
      }}
      themeButton={<ThemeSwitcher theme={theme} onToggle={toggleTheme} />}
    />
  )
}
